import {AttrsSchema, Component, ConfigOptions, jsx} from 'panel';
import isEqual from 'lodash/isEqual';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {FlowsTopPathsResponse} from 'common/report/funnels/models/api-types';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {FlowsBookmark} from 'common/report/flows/models/types';
import {FlowsReport} from 'common/report/flows/models/report';
import {ContextMenuEl} from 'common/widgets/context-menu';
import {SectionItem} from 'common/components/items-menu/types';
import {ScrollControl, ScrollControlEl} from 'common/widgets/scroll-control';
import {TextHighlightEl} from 'common/components/text-highlight';
import {
  ConversionColumnViz,
  ConversionColumnVizEl,
  StepBarContextMenuEvent,
} from 'common/charts/visualization/conversion-column-viz';
import {ScrollSyncer} from 'common/util/scroll-syncer';
import {ParamsAndResults} from 'common/report/types';

import {TopPathsFlow} from './models/top-paths-flow';
import css from './index.inline.styl';
import {getCommonConversionVizSteps, getOverallConversionRate} from './util';
import {TopPathsFlowStep} from './models/top-paths-flow-step';

interface FlowsTopPathsChartAttrs {
  loaded: boolean;
  'path-height'?: number;
}

type StepPopoverCallback = (node: TopPathsFlowStep) => {options: SectionItem[]; breakdownOptions?: UnknownObj[]};

export interface FlowsTopPathsChartJsxProps {
  attrs: FlowsTopPathsChartAttrs;
  props: {
    stepPopoverCallback: StepPopoverCallback;
    paramsAndResults: ParamsAndResults<FlowsBookmark, FlowsTopPathsResponse>;
  };
}

interface FlowsTopPathsChartState {
  report: FlowsReport | null;
  topPathsFlows: TopPathsFlow[] | null;
  popoverStep: TopPathsFlowStep;
  popoverStyles: {top: string; left: string};
  progressivePage: number;
}

const PAGE_SIZE = 5;

export class FlowsTopPathsChart extends Component<
  FlowsTopPathsChartState,
  FlowsTopPathsChartAttrs,
  any,
  any,
  ChartsContextRegistry
> {
  private _flowsResponse: FlowsTopPathsResponse;
  private _bookmarkParams: FlowsBookmark;
  private _stepPopoverCallback: StepPopoverCallback;
  private scrollSyncer: ScrollSyncer;
  private progressiveIntersectionObserver: IntersectionObserver = null;

  private firstChartEl?: ConversionColumnViz;

  private contextMenuKey = 0;

  static get attrsSchema(): AttrsSchema<FlowsTopPathsChartAttrs> {
    return {
      ...super.attrsSchema,
      loaded: `boolean`,
      'path-height': {
        type: `number`,
        description: `Height of single path in pixels. If not provided, fits height of container for scrollability.`,
      },
    };
  }

  connectedCallback() {
    this.processData();
    this.scrollSyncer = new ScrollSyncer({followerClass: null});
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.scrollSyncer?.reset();
    this.progressiveIntersectionObserver?.disconnect();
  }

  onProgressiveMarkerInserted(el: HTMLElement) {
    this.progressiveIntersectionObserver?.disconnect();

    this.progressiveIntersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.intersectionRatio > 0) {
          this.update({progressivePage: this.state.progressivePage + 1});
          requestAnimationFrame(() => this.registerScrollSyncer());
        }
      },
      {
        root: null,
        threshold: 0.1,
      },
    );

    this.progressiveIntersectionObserver.observe(el);
  }

  get config(): ConfigOptions<FlowsTopPathsChartState, any, ChartsContextRegistry> {
    return {
      useShadowDom: true,
      defaultState: {
        report: null,
        topPathsFlows: null,
        popoverStep: null,
        popoverStyles: null,
        progressivePage: 0,
      },
      contexts: [`urlManager` as const, `currentProject` as const],
      css,
      template: () => this.renderTemplate(),
    };
  }

  public get flowsResponse() {
    return this._flowsResponse;
  }

  public get bookmarkParams() {
    return this._bookmarkParams;
  }

  public set paramsAndResults({params, results}: ParamsAndResults<FlowsBookmark, FlowsTopPathsResponse>) {
    if (!isEqual(this._bookmarkParams, params) || !isEqual(this._flowsResponse, results)) {
      this._flowsResponse = results;
      this._bookmarkParams = params;
      this.processData();
    }
  }

  public set stepPopoverCallback(cb: StepPopoverCallback) {
    this._stepPopoverCallback = cb;
  }

  public get stepPopoverCallback() {
    return this._stepPopoverCallback;
  }

  private onFirstChartElInserted(vnode) {
    this.firstChartEl = vnode.elm as ConversionColumnViz;
    requestAnimationFrame(() => this.update());
  }

  private registerScrollSyncer() {
    this.scrollSyncer?.reset();
    Array.from(this.el.querySelectorAll(`mp-conversion-column-viz`)).forEach((elem: ConversionColumnViz) => {
      const scrollBox = elem.getScrollContainer();
      this.scrollSyncer.register(scrollBox);
      const parent = elem.parentElement;
      const scrollControl = parent.querySelector(`mp-scroll-control`);
      (scrollControl as ScrollControl).mousemoveEl = parent;
      (scrollControl as ScrollControl).scrollEl = scrollBox;

      if (this.firstChartEl) {
        scrollBox.scrollLeft = this.firstChartEl.getScrollContainer().scrollLeft;
      }
    });
  }

  private processData(): void {
    if (!(this.flowsResponse && this.bookmarkParams && this.isConnected)) {
      return;
    }
    const report = FlowsReport.fromBookmarkParams(this.bookmarkParams);
    const topPathsFlows = (this.flowsResponse.flows || []).map((flow) => new TopPathsFlow(flow));

    this.update({report, topPathsFlows});

    requestAnimationFrame(() => this.registerScrollSyncer());
  }

  private onStepBarContextMenu(ev: StepBarContextMenuEvent) {
    if (!this.stepPopoverCallback) {
      return;
    }
    const {originalMouseEvent, conversionStep} = ev.detail;
    const {pageX, pageY} = originalMouseEvent;

    const popoverStep = conversionStep.extras as TopPathsFlowStep;

    const parentRect = this.offsetParent.getBoundingClientRect();
    const gap = 12;
    const left = pageX - parentRect.x - window.scrollX + gap;
    const top = pageY - parentRect.y - window.scrollY + gap;

    this.update({popoverStep, popoverStyles: {left: `${left}px`, top: `${top}px`}});
  }

  private handlePopoverAction(ev) {
    this.dispatchEvent(
      new CustomEvent(`stepPopoverAction`, {
        detail: {action: ev.detail.action, step: this.state.popoverStep, value: ev.detail.value},
      }),
    );
  }

  private handlePopoverClose() {
    this.update({popoverStep: null, popoverStyles: null});
  }

  private renderPopover() {
    if (!this.state.popoverStep || !this.stepPopoverCallback) {
      return <></>;
    }

    const {options, breakdownOptions} = this.stepPopoverCallback(this.state.popoverStep);
    return (
      <ContextMenuEl
        attrs={{
          'breakdown-options': breakdownOptions,
          'menu-items': options,
          'breakdown-event': this.state.popoverStep.event,
          'relative-positioning': true,
          open: true,
          position: {left: 0, top: 0},
          'project-id': this.getContext(`currentProject`).get().value?.id,
        }}
        style={this.state.popoverStyles}
        key={this.contextMenuKey++}
        on={{
          action: (ev: CustomEvent) => this.handlePopoverAction(ev),
          close: () => this.handlePopoverClose(),
        }}
      />
    );
  }

  private renderTemplate() {
    const displayedPaths = this.state.topPathsFlows.slice(0, (this.state.progressivePage + 1) * PAGE_SIZE);

    return (
      <div
        sel='.flows-top-paths-chart'
        class={{loading: !this.attr(`loaded`)}}
        style={this.attr(`path-height`) ? {'--flows-top-path-height': `${this.attr(`path-height`)}px`} : {}}
      >
        {displayedPaths.map((topPathsFlow, idx) => {
          const conversionRate = getOverallConversionRate(topPathsFlow);
          const conversionSteps = getCommonConversionVizSteps(topPathsFlow);

          return (
            <div sel='.steps-wrapper'>
              <ScrollControlEl attrs={{'scroll-by': this.firstChartEl?.getStepWidth()}} />
              <div sel='.conversion'>
                <TextHighlightEl attrs={{text: conversionRate}} />
                overall conversion
              </div>
              <ConversionColumnVizEl
                key={idx}
                attrs={{
                  'enable-popover-menu': !!this.stepPopoverCallback,
                  'popover-menu-open': !!this.state.popoverStep,
                }}
                props={{conversionSteps}}
                hook={idx === 0 ? {insert: (vnode) => this.onFirstChartElInserted(vnode)} : {}}
                on={{stepBarContextMenu: (ev) => this.onStepBarContextMenu(ev)}}
              />
            </div>
          );
        })}

        <div
          sel='.progressive-marker'
          hook={{insert: (vnode) => this.onProgressiveMarkerInserted(vnode.elm as HTMLElement)}}
        />

        {this.renderPopover()}
      </div>
    );
  }
}

export const FlowsTopPathsChartWidgetEl = defineAndMakeJsxFactory(
  `mp-flows-top-paths-chart-widget`,
  FlowsTopPathsChart,
);
export const FlowsTopPathsChartWidgetReact = panel2react(`mp-flows-top-paths-chart-widget`, FlowsTopPathsChart);
